'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';

import { T } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { ChannelDropdown } from '../../components/channel-dropdown';
import { channelHintKeyMap, channelPlaceholderMap } from '../../constants';
import { channelVerificationReportPageBunja, descriptionMaxLength } from './state';

export const ChannelVerificationReportPage = () => {
  const t = useTranslations();
  const { channelAtom, sourceAtom, descriptionAtom, isValidAtom, isReportingAtom, submit } = useBunja(
    channelVerificationReportPageBunja,
  );

  const channelRaw = useAtomValue(channelAtom);
  const setChannel = useSetAtom(channelAtom);
  const channel = channelRaw ?? undefined;
  const source = useAtomValue(sourceAtom);
  const setSource = useSetAtom(sourceAtom);
  const description = useAtomValue(descriptionAtom);
  const setDescription = useSetAtom(descriptionAtom);
  const isValid = useAtomValue(isValidAtom);
  const isReporting = useAtomValue(isReportingAtom);

  return (
    <main
      className={stack({
        w: '100%',
        mx: 'auto',
        maxW: 'desktopMaxWidth',
        minH: 'minHeightWithoutNavigation',
        px: { base: 16, tablet: 32 },
        py: { base: 32, tablet: 80 },
        gap: { base: 32, tablet: 40 },
        smallDesktop: { boxSizing: 'content-box' },
      })}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className={stack({
          gap: 32,
          maxW: 600,
          w: '100%',
          mx: 'auto',
          rounded: 16,
          tablet: {
            p: 32,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-2',
          },
        })}
      >
        <p className={css({ textStyle: 'Title/L', color: 'inverse.white' })}>
          <T id='VIP.r260316.Phishing_attack_report' />
        </p>
        {/* Channel */}
        <div className={stack({ gap: 8 })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <T id='VIP.r260316.Channel' />
          </p>
          <ChannelDropdown
            selected={channel}
            onChange={setChannel}
            placeholder={<T id='VIP.r260316.Select_channel' />}
            css={{ w: '100%', py: 13, px: 16 }}
          />
        </div>

        {/* Source */}
        <div className={stack({ gap: 8 })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <T id='VIP.r260316.Source' />
          </p>
          <input
            type='text'
            value={source}
            onChange={(e) => setSource(e.currentTarget.value)}
            placeholder={channel ? channelPlaceholderMap[channel] : t('VIP.r260316.Enter_contact_details')}
            className={css({
              py: 13,
              px: 16,
              borderColor: 'border.default.level-3',
              borderWidth: '1px',
              borderStyle: 'solid',
              rounded: 8,
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-5',
              _placeholder: { color: 'content.default.level-1' },
            })}
          />
          {channel && (
            <div className={hstack({ gap: 8, textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <CautionCircleIcon size={16} />
              <p>{t(channelHintKeyMap[channel])}</p>
            </div>
          )}
        </div>

        {/* Description */}
        <div className={stack({ gap: 8 })}>
          <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
            <T id='VIP.r260316.Description_optional' />
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            placeholder={t('VIP.r260316.Add_any_additional_details_about_the_suspicious_message_or_source')}
            rows={5}
            className={css({
              py: 13,
              px: 16,
              bg: 'transparent',
              borderColor:
                description.length > descriptionMaxLength ? 'border.state.negative' : 'border.default.level-3',
              borderWidth: '1px',
              borderStyle: 'solid',
              rounded: 8,
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-5',
              resize: 'none',
              _placeholder: { color: 'content.default.level-1' },
            })}
          />
          <p
            className={css({
              textStyle: 'Text/S/Regular',
              color: description.length > descriptionMaxLength ? 'content.state.negative' : 'content.default.level-2',
              textAlign: 'right',
            })}
          >
            {description.length}/{descriptionMaxLength}
          </p>
        </div>

        {/* Privacy Notice */}
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
          <T
            id='VIP.r260316.By_submitting_this_form_you_acknowledge_that_your_information_will_be_processed'
            values={{
              privacyPolicy: (
                <a
                  href='/policies/privacy'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={css({
                    textStyle: 'Text/M/Strong',
                    color: 'content.accent.info',
                    textDecoration: 'underline',
                  })}
                >
                  <T id='Navigation.r250401.Privacy_policy' />
                </a>
              ),
              termsOfUse: (
                <a
                  href='/policies/terms'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={css({
                    textStyle: 'Text/M/Strong',
                    color: 'content.accent.info',
                    textDecoration: 'underline',
                  })}
                >
                  <T id='Navigation.r250401.Terms_of_use' />
                </a>
              ),
            }}
          />
        </p>

        {/* Divider + Submit */}
        <div className={stack({ gap: 24 })}>
          <div
            className={css({ borderStyle: 'solid', borderTopWidth: 1, borderColor: 'border.default.level-1', h: 12 })}
          />
          <div className={hstack({ justify: 'flex-end' })}>
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              label={t('VIP.r260316.Report_button')}
              disabled={!isValid}
              isLoading={isReporting}
              withSpinner
              htmlType='submit'
            />
          </div>
        </div>
      </form>
    </main>
  );
};
